import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/api-error";

/**
 * SimpleCrudTab — componente riutilizzabile per anagrafiche a struttura
 * uniforme (nome + descrizione + attivo/archiviato).
 *
 * Props:
 *  - resourceName  singolare (es. "metodo di pagamento")
 *  - resourceLabel plurale (es. "Metodi di pagamento")
 *  - fields        array di { key, label, type?, placeholder?, required?, max? }
 *  - loader        () => Promise<items[]>  (deve accettare includeArchived opz)
 *  - onCreate/onUpdate/onDelete/onReactivate  API functions
 *  - columns       array di { header, accessor(row), width? }
 *  - testIdPrefix  es. "pm" | "bank" | "tag"
 */
export function SimpleCrudTab({
  resourceName,
  resourceLabel,
  fields,
  loader,
  onCreate,
  onUpdate,
  onDelete,
  onReactivate,
  columns,
  testIdPrefix,
  extraDescription,
  disableDelete,
  deleteDisabledLabel,
}) {
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ mode: "create", id: null });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const data = await loader({ includeArchived: showArchived });
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, [showArchived]);

  const openCreate = () => {
    const empty = { mode: "create", id: null };
    for (const f of fields) empty[f.key] = f.default || "";
    setForm(empty);
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const filled = { mode: "edit", id: row.id };
    for (const f of fields) filled[f.key] = row[f.key] ?? f.default ?? "";
    setForm(filled);
    setError("");
    setDialogOpen(true);
  };

  const close = () => {
    if (saving) return;
    setDialogOpen(false);
    setError("");
  };

  const submit = async () => {
    setError("");
    // Validazione lato client
    for (const f of fields) {
      if (f.required && !(form[f.key] || "").toString().trim()) {
        setError(`${f.label} è obbligatorio`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {};
      for (const f of fields) {
        const v = form[f.key];
        payload[f.key] = v === "" ? null : v;
      }
      if (form.mode === "create") {
        await onCreate(payload);
        toast.success(`${resourceName} creato`);
      } else {
        await onUpdate(form.id, payload);
        toast.success(`${resourceName} aggiornato`);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const archive = async (row) => {
    if (!window.confirm(`Archiviare "${row.name}"?`)) return;
    try {
      await onDelete(row.id);
      toast.success(`${resourceName} archiviato`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reactivate = async (row) => {
    try {
      await onReactivate(row.id);
      toast.success(`${resourceName} ripristinato`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activeCount = items.filter((i) => i.is_active).length;

  return (
    <div data-testid={`${testIdPrefix}-tab`} className="mt-4">
      {extraDescription && (
        <p className="mb-3 text-[12px] leading-snug text-stone-500">
          {extraDescription}
        </p>
      )}
      <div className="mb-3 flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-[12px] text-stone-600">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid={`${testIdPrefix}-show-archived`}
          />
          Mostra archiviati
        </label>
        <Button
          data-testid={`${testIdPrefix}-btn-new`}
          onClick={openCreate}
          className="gap-1.5"
        >
          <Plus size={14} strokeWidth={2} />
          Nuovo {resourceName}
        </Button>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead
                  key={c.header}
                  className={c.width ? c.width : undefined}
                >
                  {c.header}
                </TableHead>
              ))}
              <TableHead className="w-24">Stato</TableHead>
              <TableHead className="w-40 text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 2}
                  className="py-8 text-center text-sm text-stone-500"
                >
                  Nessun {resourceName}.
                </TableCell>
              </TableRow>
            )}
            {items.map((row) => (
              <TableRow
                key={row.id}
                data-testid={`${testIdPrefix}-row-${row.id}`}
                className={!row.is_active ? "opacity-60" : undefined}
              >
                {columns.map((c) => (
                  <TableCell key={c.header}>{c.accessor(row)}</TableCell>
                ))}
                <TableCell>
                  {row.is_active ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-800">
                      Attivo
                    </span>
                  ) : (
                    <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-stone-500">
                      Archiviato
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`${testIdPrefix}-edit-${row.id}`}
                      onClick={() => openEdit(row)}
                      className="h-7 gap-1 px-2"
                    >
                      <Pencil size={11} strokeWidth={2} />
                      Modifica
                    </Button>
                    {row.is_active ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`${testIdPrefix}-archive-${row.id}`}
                        onClick={() => archive(row)}
                        disabled={disableDelete?.(row)}
                        title={disableDelete?.(row) ? (deleteDisabledLabel || "Non eliminabile") : ""}
                        className="h-7 gap-1 px-2 text-rose-700 hover:text-rose-900 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Trash2 size={11} strokeWidth={2} />
                        Archivia
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`${testIdPrefix}-reactivate-${row.id}`}
                        onClick={() => reactivate(row)}
                        className="h-7 gap-1 px-2 text-emerald-700 hover:text-emerald-900"
                      >
                        <RotateCcw size={11} strokeWidth={2} />
                        Ripristina
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2 text-[11px] text-stone-500">
        {activeCount} {activeCount === 1 ? "voce attiva" : "voci attive"}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => (o ? null : close())}>
        <DialogContent
          className="max-w-md"
          data-testid={`${testIdPrefix}-dialog`}
        >
          <DialogHeader>
            <DialogTitle>
              {form.mode === "create"
                ? `Nuovo ${resourceName}`
                : `Modifica ${resourceName}`}
            </DialogTitle>
            <DialogDescription>
              {resourceLabel} — aggiungi, modifica o archivia liberamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.key} className="space-y-1">
                <Label htmlFor={`${testIdPrefix}-${f.key}`} className="text-xs">
                  {f.label}
                  {f.required && <span className="text-rose-600"> *</span>}
                </Label>
                <Input
                  id={`${testIdPrefix}-${f.key}`}
                  type={f.type || "text"}
                  data-testid={`${testIdPrefix}-input-${f.key}`}
                  placeholder={f.placeholder}
                  maxLength={f.max}
                  value={form[f.key] || ""}
                  onChange={(e) =>
                    setForm({ ...form, [f.key]: e.target.value })
                  }
                />
                {f.hint && (
                  <p className="text-[10px] text-stone-400">{f.hint}</p>
                )}
              </div>
            ))}
            {error && (
              <div
                data-testid={`${testIdPrefix}-error`}
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
              >
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={saving}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              data-testid={`${testIdPrefix}-submit`}
            >
              {saving ? "Salvo…" : form.mode === "create" ? "Crea" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
